import { modelState } from "@/utils/modelState";
import { AlwaysCondition } from "@/Vouchers/AlwaysCondition";
import { BookingDurationCondition } from "@/Vouchers/BookingDurationCondition";
import { BookingPeriodCondition } from "@/Vouchers/BookingPeriodCondition";
import { DaysUntilArrivalCondition } from "@/Vouchers/DaysUntilArrivalCondition";
import { NeverCondition } from "@/Vouchers/NeverCondition";
import { OccupancyCondition } from "@/Vouchers/OccupancyCondition";
import { OrderItemCondition } from "@/Vouchers/OrderItemCondition";
import { QueryBuilder } from "@/Vouchers/QueryBuilder";
import { ReturningGuestCondition } from "@/Vouchers/ReturningGuestCondition";
import { LocalExpatGuestCondition } from "@/Vouchers/LocalExpatGuestCondition";
import { ValidityPeriodCondition } from "@/Vouchers/ValidityPeriodCondition";

export namespace BaseRateModels {

    export function buildConditionRegistry(
        roomTypes: { key: string; name: string }[],
        programTypes: { key: string; name: string }[],
        occupantTypes: { key: string; name: string }[]): QueryBuilder.ConditionRegistryEntry[] {
        return [
            BookingPeriodCondition.registryEntry,
            OccupancyCondition.registryEntry,
            ValidityPeriodCondition.registryEntry,
            AlwaysCondition.registryEntry,
            NeverCondition.registryEntry,
            OrderItemCondition.buildRegistryEntry(roomTypes, programTypes, occupantTypes),
            ReturningGuestCondition.registryEntry,
            LocalExpatGuestCondition.registryEntry,
            BookingDurationCondition.registryEntry,
            DaysUntilArrivalCondition.registryEntry
        ];
    }

    export function deserialize(
        dto: BaseRateModelDto,
        conditionRegistry: QueryBuilder.ConditionRegistryEntry[]) {
        const model: BaseRateModel = {
            baseRateId: dto.baseRateId,
            name: modelState(dto.name),
            description: modelState(dto.description),
            baseRates: deserializeBaseRates(dto.baseRates),
            enableCondition: QueryBuilder.deserialize(dto.enableConditionJson, conditionRegistry),
            visibleInReservations: modelState(dto.visibleInReservations)
        };

        return model
    }

    export function deserializeBaseRates(dto: BaseRatesModelDto) {
        return {
            grossAmount: modelState(dto.grossAmount),
            netAmount: modelState(dto.netAmount),
            serviceChargeAmount: modelState(dto.serviceChargeAmount),
            vatAmount: modelState(dto.vatAmount)
        }
    }

    export function serialize(
        model: BaseRateModel,
        conditionRegistry: QueryBuilder.ConditionRegistryEntry[]) {
        const dto: SaveBaseRateModelDto = {
            name: model.name.value,
            description: model.description.value,
            baseRates: serializeBaseRates(model.baseRates),
            enableConditionJson: JSON.stringify(QueryBuilder.getRootNodeDtoModel(model.enableCondition, conditionRegistry)),
            visibleInReservations: model.visibleInReservations.value
        };
        return dto;
    }

    export function serializeBaseRates(model: BaseRatesModel) {
        return {
            grossAmount: model.grossAmount.value,
            netAmount: model.netAmount.value,
            serviceChargeAmount: model.serviceChargeAmount.value,
            vatAmount: model.vatAmount.value
        }
    }

    export interface BaseRateModel {
        baseRateId: string;
        name: modelState<string>;
        description: modelState<string>;
        baseRates: BaseRatesModel;
        enableCondition: QueryBuilder.GroupNode;
        visibleInReservations: modelState<boolean>;
    }

    export interface BaseRatesModel {
        grossAmount: modelState<number>;
        netAmount: modelState<number>;
        serviceChargeAmount: modelState<number>;
        vatAmount: modelState<number>;
    }

    export interface BaseRateModelDto {
        baseRateId: string;
        name: string;
        description: string;
        baseRates: BaseRatesModelDto;
        enableConditionJson: string;
        visibleInReservations: boolean;
    }

    export interface BaseRatesModelDto {
        grossAmount: number;
        netAmount: number;
        serviceChargeAmount: number;
        vatAmount: number;
    }

    export interface SaveBaseRateModelDto {
        name: string;
        description: string;
        baseRates: BaseRatesModelDto;
        enableConditionJson: string;
        visibleInReservations: boolean;
    }
}